/**
 * Google Calendar integration for tournament scheduling.
 * - Check free/busy for availability
 * - Create match events when matches are scheduled
 *
 * Setup required:
 * 1. Calendar scopes are requested only via requestCalendarAccess when user opts into calendar sync (Connect/Resync)
 * 2. Enable Google Calendar API in Google Cloud Console for your project
 * 3. Pass credential.accessToken from signInWithPopup result for API calls
 *
 * The access token is short-lived. For server-side event creation when matches
 * are scheduled, consider storing a refresh token via a Cloud Function that
 * exchanges the access token, or trigger "Add to Calendar" client-side when
 * the player views their scheduled match.
 */

import { toUtcMs } from '../utils/dateTimeUtils';

const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';

/**
 * Extract start datetime in ms from match (scheduledAt, scheduledDate, scheduledTime).
 * scheduledAt from API is already UTC ms (preferred). Handles Firestore Timestamp, serialized {seconds}, Date.
 * @returns {number|null} UTC ms or null if invalid
 */
function getMatchStartMs(match) {
  const at = match.scheduledAt ?? match.scheduledDate;
  const dateMs = toUtcMs(at);
  if (dateMs == null || isNaN(dateMs) || dateMs <= 0) return null;
  const timeStr = (match.scheduledTime || '18:00').toString().trim();
  const [h = 0, min = 0] = timeStr.split(':').map(Number);
  const d = new Date(dateMs);
  d.setHours(h, min, 0, 0);
  return d.getTime();
}

/**
 * Fetch free/busy slots from user's primary calendar for a date range.
 * @param {string} accessToken - Google OAuth access token (from credential.accessToken)
 * @param {string} timeMin - ISO 8601
 * @param {string} timeMax - ISO 8601
 * @returns {Promise<{busy: Array<{start:string,end:string}>}>}
 */
export async function getFreeBusy(accessToken, timeMin, timeMax) {
  const res = await fetch(`${CALENDAR_API_BASE}/freeBusy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      timeMin,
      timeMax,
      items: [{ id: 'primary' }],
    }),
  });
  if (!res.ok) throw new Error('Google Calendar free/busy failed');
  const data = await res.json();
  const primary = data.calendars?.primary;
  return { busy: primary?.busy || [] };
}

/**
 * Build summary and optional attendees for a match calendar event.
 * @param {object} match - Match with player/team names
 * @param {boolean} [isReferee] - If true, prefix summary with "Referee: "
 * @returns {{ summary: string, attendees?: Array<{ email: string }> }}
 */
function buildEventSummaryAndAttendees(match, isReferee = false, attendeeEmails = []) {
  const p1 = match.player1Name || match.team1Name || 'Player 1';
  const p2 = match.player2Name || match.team2Name || 'Player 2';
  const summary = isReferee ? `Referee: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
  const attendees = attendeeEmails.length > 0
    ? attendeeEmails.map((email) => ({ email: email.trim() })).filter((a) => a.email)
    : undefined;
  return { summary, attendees };
}

/**
 * Create a calendar event for a match.
 * @param {string} accessToken - Google OAuth access token
 * @param {object} match - { scheduledDate, scheduledTime, matchDuration, player1Name, player2Name, venueName, ... }
 * @param {string} tournamentName
 * @param {{ isReferee?: boolean, attendeeEmails?: string[] }} [options] - isReferee: prefix "Referee: " on summary; attendeeEmails: send invites for RSVP
 * @returns {Promise<{htmlLink?: string, id?: string}>}
 */
export async function createMatchEvent(accessToken, match, tournamentName, options = {}) {
  const startMs = getMatchStartMs(match);
  if (startMs == null || isNaN(startMs)) throw new Error('Invalid time value');
  const start = new Date(startMs);
  const durationMins = match.matchDuration || 30;
  const end = new Date(start.getTime() + durationMins * 60 * 1000);

  const { summary, attendees } = buildEventSummaryAndAttendees(
    match,
    options.isReferee === true,
    options.attendeeEmails || []
  );
  const venueLocation = (match.venueLocation || '').trim();
  const venueLabel = match.venueName ? `${match.venueName}${match.courtName ? ` (${match.courtName})` : ''}` : '';
  const locationStr = venueLocation || venueLabel;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeStr = start.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  const descriptionLines = [
    'Table Tennis match',
    tournamentName ? `Tournament: ${tournamentName}` : '',
    locationStr ? `Location: ${locationStr}` : '',
    `Time: ${timeStr}`,
    `Duration: ${durationMins} min`,
  ].filter(Boolean);
  const description = descriptionLines.join('\n');

  const body = {
    summary,
    description,
    location: locationStr || undefined,
    start: { dateTime: start.toISOString(), timeZone },
    end: { dateTime: end.toISOString(), timeZone },
  };
  if (attendees && attendees.length > 0) body.attendees = attendees;

  const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Google Calendar create event failed');
  const event = await res.json();
  return { htmlLink: event.htmlLink, id: event.id };
}

/**
 * Update an existing calendar event with new start/end (when match is rescheduled).
 * @param {string} accessToken - Google OAuth access token
 * @param {string} eventId - Calendar event ID from createMatchEvent
 * @param {object} match - Match with new scheduledDate, scheduledTime, matchDuration
 * @param {string} tournamentName
 * @param {{ isReferee?: boolean }} [options] - isReferee: prefix "Referee: " on summary
 * @returns {Promise<{htmlLink?: string, id?: string}>}
 */
export async function updateMatchEvent(accessToken, eventId, match, tournamentName, options = {}) {
  const startMs = getMatchStartMs(match);
  if (startMs == null || isNaN(startMs)) throw new Error('Invalid time value');
  const start = new Date(startMs);
  const durationMins = match.matchDuration || 30;
  const end = new Date(start.getTime() + durationMins * 60 * 1000);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { summary } = buildEventSummaryAndAttendees(match, options.isReferee === true, []);
  const venueLocation = (match.venueLocation || '').trim();
  const venueLabel = match.venueName ? `${match.venueName}${match.courtName ? ` (${match.courtName})` : ''}` : '';
  const locationStr = venueLocation || venueLabel;
  const timeStr = start.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  const descriptionLines = [
    'Table Tennis match',
    tournamentName ? `Tournament: ${tournamentName}` : '',
    locationStr ? `Location: ${locationStr}` : '',
    `Time: ${timeStr}`,
    `Duration: ${durationMins} min`,
  ].filter(Boolean);
  const description = descriptionLines.join('\n');

  const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events/${eventId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      summary,
      description,
      location: locationStr || undefined,
      start: { dateTime: start.toISOString(), timeZone },
      end: { dateTime: end.toISOString(), timeZone },
    }),
  });
  if (!res.ok) throw new Error('Google Calendar update event failed');
  const event = await res.json();
  return { htmlLink: event.htmlLink, id: event.id };
}

/**
 * Delete a calendar event (e.g. when a match is deleted or cancelled).
 * @param {string} accessToken - Google OAuth access token
 * @param {string} eventId - Calendar event ID
 * @returns {Promise<void>}
 */
export async function deleteCalendarEvent(accessToken, eventId) {
  if (!eventId) return;
  const res = await fetch(
    `${CALENDAR_API_BASE}/calendars/primary/events/${encodeURIComponent(eventId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
  if (!res.ok && res.status !== 404) throw new Error('Google Calendar delete event failed');
}

/**
 * Check if a specific datetime is free according to free/busy response.
 */
export function isSlotFreeInCalendar(slotStart, slotEnd, busy) {
  const startMs = new Date(slotStart).getTime();
  const endMs = new Date(slotEnd).getTime();
  for (const b of busy || []) {
    const bStart = new Date(b.start).getTime();
    const bEnd = new Date(b.end).getTime();
    if (startMs < bEnd && endMs > bStart) return false;
  }
  return true;
}

/**
 * Fetch a calendar event by ID. Use to check if an invitee has declined.
 * Requires the calendar owner's access token. For match events we create per user,
 * each user's token is needed to check their own event's response (e.g. if they declined).
 *
 * @param {string} accessToken - Google OAuth access token (for the calendar owner)
 * @param {string} eventId - Calendar event ID
 * @returns {Promise<{ id: string, summary?: string, attendees?: Array<{ email: string, responseStatus?: string }>, status?: string }|null>}
 */
export async function getCalendarEvent(accessToken, eventId) {
  if (!eventId) return null;
  const res = await fetch(
    `${CALENDAR_API_BASE}/calendars/primary/events/${encodeURIComponent(eventId)}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
  if (!res.ok) return null;
  const event = await res.json();
  return {
    id: event.id,
    summary: event.summary,
    status: event.status,
    attendees: (event.attendees || []).map((a) => ({
      email: a.email,
      responseStatus: a.responseStatus,
    })),
  };
}

/**
 * Check if any attendee has declined the event (or if the event was cancelled).
 * Use after getCalendarEvent when you have stored event IDs (e.g. matchCalendarEvents).
 *
 * @param {object} event - from getCalendarEvent
 * @returns {boolean} true if event is cancelled or any attendee response is 'declined'
 */
export function isEventDeclinedOrCancelled(event) {
  if (!event) return true;
  if (event.status === 'cancelled') return true;
  const declined = (event.attendees || []).some(
    (a) => (a.responseStatus || '').toLowerCase() === 'declined'
  );
  return declined;
}
